/**
 * A single-page application that changes content without moving focus is silent to a screen
 * reader. Each screen calls `useScreen`, which sets the title, moves focus to the `h1` and
 * announces the change politely.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

let announce: ((message: string) => void) | null = null;

export function RouteAnnouncer() {
  const ref = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    announce = (message: string) => {
      if (ref.current) ref.current.textContent = message;
    };
    return () => {
      announce = null;
    };
  }, []);

  return <p ref={ref} className="visually-hidden" role="status" aria-live="polite" />;
}

/**
 * Sets the document title, announces the screen and moves focus to its heading. The heading ref
 * it returns is attached by the screen to its own `h1`.
 */
export function useScreen(leaf: string | null) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The *path*, never `location.key`: React Router mints a fresh key on every navigation,
  // including a `replace`. Filters live in the query string, so keying on it moved focus to the
  // heading 250ms after each keystroke in a search box — the caret left the field mid-word and a
  // screen reader re-announced the page. A query-string change is the same screen.
  const { pathname } = useLocation();
  const announced = useRef<string | null>(null);

  useEffect(() => {
    if (leaf === null) return;
    document.title = `${leaf} · The Law`;
    if (announced.current === `${pathname}:${leaf}`) return;
    announced.current = `${pathname}:${leaf}`;
    announce?.(leaf);
    headingRef.current?.focus({ preventScroll: true });
  }, [leaf, pathname]);

  return headingRef;
}
